/* ──────────────────────────────────────────────────────────────────────────────
   Faz 5 — Depozito İade İrsaliyesi Detayı  ·  /deposit-returns/:id

   SALT-OKUNUR (#282 + iş gereği): bu bir SAHA BELGESİDİR — plasiyerin kestiği, depozito
   stoğunu düşürmüş bir kayıt. Backoffice onu DÜZENLEMEZ; taslaksa İPTAL edebilir.
   Kesilmiş (Gönderildi) belge İPTAL EDİLEMEZ: mal fiziksel olarak araçtan çıkmış ve
   satıcıya teslim edilmiştir — stoğu geri almak sahada olmayan bir gerçeği uydurmak olur.
   Düzeltme AYRI bir belgeyle yapılır (backend de aynı kuralı uygular).

   ══ TUTAR ALANI YOKTUR ════════════════════════════════════════════════════════
   Ne başlıkta ne kalemlerde para gösterilir; "₺0,00" bile yazılmaz (spec §6). Tutarın
   yerinde sabit ibare durur: "Bedelsiz — depozito iadesi". Bu bilinçli bir boşluktur.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { DetailShell, Section, Grid } from './detail/DetailShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { fmtDate, fmtDateTime, fmtQty, unwrap } from './returnShared';
import {
  RETURNS_API, PRINT_STATEMENT, statusLabel, statusTone, isPosted, isCancellable,
  damageRate, fmtPct, DAMAGE_RATE_THRESHOLD,
} from './depositShared';

const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  td: { padding: '9px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' },
  num: { textAlign: 'right' },
  danger: { color: 'var(--danger)', fontWeight: 800 },
  ok: { color: 'var(--success, #16a34a)', fontWeight: 700 },
  muted: { color: muted },
  empty: { padding: 12, fontSize: 13, color: muted },
  fact: {
    display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px',
    background: 'var(--bg-card, #fff)', border, borderRadius: 10,
  },
  factLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, color: muted },
  factValue: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  statement: {
    marginTop: 12, padding: '12px 14px', borderRadius: 10, border,
    background: 'var(--bg-subtle, rgba(15,23,42,0.03))',
    fontSize: 13, color: 'var(--text-primary, #0f172a)',
  },
};

function Fact({ label, value, tone }) {
  return (
    <div style={st.fact}>
      <span style={st.factLabel}>{label}</span>
      <span style={{ ...st.factValue, ...(tone === 'bad' ? st.danger : null) }}>{value ?? '-'}</span>
    </div>
  );
}

function ConfirmCancelModal({ documentNo, busy, onClose, onSubmit }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>İrsaliyeyi İptal Et{documentNo ? ` — ${documentNo}` : ''}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          <p style={{ margin: 0, fontSize: 13, color: muted, lineHeight: 1.6 }}>
            Yalnız TASLAK irsaliye iptal edilebilir. Taslak henüz postlanmamıştır: stok da depozito
            bakiyesi de bu belgeden etkilenmemiştir, iptal fiziksel bir olayı geri almaz.
          </p>
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
          <button style={s.btnDanger} onClick={onSubmit} disabled={busy}>
            {busy ? 'İşleniyor…' : 'İrsaliyeyi İptal Et'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DepositReturnDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePageActions();
  usePageMeta('Depozito İade Detayı', 'Satıcı cari, araç, kalemler ve kırık adetleri. Belge tutar taşımaz.');

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/vendors/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, v, w]) => {
      setRepresentatives(unwrapLookupItems(r));
      setVendors(unwrapLookupItems(v));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${RETURNS_API}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Depozito iade irsaliyesi alınamadı');
      const d = unwrap(await res.json());
      setHeader(d?.header || null);
      setItems(d?.items || []);
    } catch (e) {
      showToast('error', e.message || 'Depozito iade irsaliyesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const nameOf = (list, guid) => list.find((x) => x.id === guid)?.name || '-';
  const repName = header ? nameOf(representatives, header.representativeId) : '-';
  const vendorLabel = header ? nameOf(vendors, header.vendorId) : '-';
  const vehicleLabel = header
    ? (warehouses.find((x) => x.id === header.warehouseId)?.name
      || warehouses.find((x) => x.id === header.warehouseId)?.textId || '-')
    : '-';

  const totals = useMemo(() => {
    const quantity = Number(header?.totalQuantity || 0);
    const damaged = Number(header?.totalDamaged || 0);
    return { quantity, damaged, rate: damageRate(quantity, damaged), lines: items.length };
  }, [header, items]);

  const highDamage = totals.rate !== null && totals.rate >= DAMAGE_RATE_THRESHOLD;
  const posted = header && isPosted(header.status);
  const cancellable = header && isCancellable(header.status);

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${RETURNS_API}/${id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'İptal başarısız');
      showToast('success', 'İrsaliye iptal edildi.');
      setConfirm(false);
      load();
    } catch (e) {
      showToast('error', e.message || 'İptal başarısız');
    } finally {
      setBusy(false);
    }
  };

  const crumb = [
    { label: 'Depozito İade İrsaliyesi', to: '/deposit-returns' },
    { label: header?.documentNo || 'Detay', current: true },
  ];

  const actionButtons = header ? (
    <>
      <button type="button" className="dps-btn" onClick={load} disabled={loading}>Yenile</button>
      {canEdit && cancellable && (
        <button type="button" className="dps-btn" onClick={() => setConfirm(true)} disabled={busy}>
          İrsaliyeyi İptal Et
        </button>
      )}
      <button type="button" className="dps-btn" onClick={() => navigate('/deposit-reconciliation')}>Mutabakat</button>
      <button type="button" className="dps-btn" onClick={() => navigate('/deposit-returns')}>Kapat</button>
    </>
  ) : null;

  const statusNode = header ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <StatusBadge tone={statusTone(header.status)}>{statusLabel(header.status)}</StatusBadge>
      {highDamage && <StatusBadge tone="bad">Yüksek kırık {fmtPct(totals.rate)}</StatusBadge>}
    </span>
  ) : null;

  return (
    <>
      <DetailShell crumb={crumb} status={statusNode} actions={actionButtons} viewActions={actionButtons}
                   loading={loading} readOnly>
        {!loading && !header && (
          <Section title="Bulunamadı"><div style={st.empty}>Depozito iade irsaliyesi bulunamadı.</div></Section>
        )}

        {header && (
          <>
            <Section icon="wallet" title="Belge"
                     subtitle="Emanetin geri verilmesi — ticari işlem değildir. Belge GİB'e gitmez, tutar taşımaz."
                     note={posted
                       ? 'Belge KESİLMİŞTİR: depozito stoğu araçtan düşmüş ve plasiyerin bakiyesi azalmıştır. Kesilmiş belge iptal edilemez — düzeltme ayrı belgeyle yapılır.'
                       : 'TASLAK: stok ve bakiye HENÜZ etkilenmemiştir. Etki belge kesildiğinde (Gönderildi) doğar.'}>
              <Grid cols={3}>
                <Fact label="İrsaliye No" value={header.documentNo || '(taslak — numara belge kesilince verilir)'} />
                <Fact label="Tarih" value={fmtDate(header.sentAt || header.createdDate)} />
                <Fact label="Durum" value={statusLabel(header.status)} />
                <Fact label="Satıcı Cari" value={vendorLabel} />
                <Fact label="Plasiyer" value={repName} />
                <Fact label="Araç Deposu" value={vehicleLabel} />
                <Fact label="Kalem / Toplam Adet" value={`${totals.lines} kalem · ${fmtQty(totals.quantity)}`} />
                {/* Kırık, iade edilenin İÇİNDEDİR — toplama EKLENMEZ (iş kuralı 3). */}
                <Fact label="Kırık Adet (iadenin içinde)" value={fmtQty(totals.damaged)} tone={highDamage ? 'bad' : undefined} />
                <Fact label="Kırık Oranı" value={fmtPct(totals.rate)} tone={highDamage ? 'bad' : undefined} />
                <Fact label="Kesim Zamanı" value={header.sentAt ? fmtDateTime(header.sentAt) : '—'} />
                <Fact label="SDM" value={header.sdmName || '—'} />
                {header.note && <Fact label="Not" value={header.note} />}
              </Grid>

              {/* TUTARIN YERİNDE DURAN İBARE — para alanı bilinçli boşluktur. */}
              <div style={st.statement}>
                <b>{PRINT_STATEMENT}</b>
                <span style={{ color: muted }}>
                  {' '}— belgede tutar/KDV alanı yoktur; karşılığı para değil, depozito bakiyesinin azalmasıdır.
                </span>
              </div>
            </Section>

            <Section icon="list" title="Kalemler"
                     subtitle="Yalnız depozito ürünleri (sepet / kasa / cam şişe). Kırık adet iade edilenin içindedir, ek değildir."
                     note={highDamage
                       ? `Bu belgede kırık oranı %${DAMAGE_RATE_THRESHOLD} eşiğini aşıyor (${fmtPct(totals.rate)}). Sürekli yüksek kırık bildiren plasiyer, ya bir taşıma sorununa ya da suistimale işaret eder.`
                       : undefined}>
              {items.length === 0 ? (
                <div style={st.empty}>Kalem yok.</div>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Ürün</th>
                      <th style={st.th}>Kod</th>
                      <th style={st.th}>Birim</th>
                      <th style={{ ...st.th, ...st.num }}>İade Adedi</th>
                      <th style={{ ...st.th, ...st.num }}>Kırık</th>
                      <th style={{ ...st.th, ...st.num }}>Kırık %</th>
                      <th style={st.th}>Not</th>
                      {/* BİRİM FİYAT / TUTAR KOLONU YOK — bilinçli boşluk. */}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => {
                      const rate = damageRate(l.quantity, l.damagedQuantity);
                      const high = rate !== null && rate >= DAMAGE_RATE_THRESHOLD;
                      return (
                        <tr key={l.id}>
                          <td style={st.td}>{l.productName || '-'}</td>
                          <td style={st.td}>{l.productCode || '-'}</td>
                          <td style={st.td}>{l.unit || 'AD'}</td>
                          <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(l.quantity)}</td>
                          <td style={{ ...st.td, ...st.num }}>{fmtQty(l.damagedQuantity)}</td>
                          <td style={{ ...st.td, ...st.num, ...(high ? st.danger : st.ok) }}>{fmtPct(rate)}</td>
                          <td style={{ ...st.td, ...st.muted }}>{l.note || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...st.td, fontWeight: 800 }} colSpan={3}>Toplam</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totals.quantity)}</td>
                      <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totals.damaged)}</td>
                      <td style={{ ...st.td, ...st.num, ...(highDamage ? st.danger : st.ok) }}>{fmtPct(totals.rate)}</td>
                      <td style={st.td} />
                    </tr>
                  </tfoot>
                </table>
              )}
            </Section>
          </>
        )}
      </DetailShell>

      {confirm && (
        <ConfirmCancelModal
          documentNo={header?.documentNo}
          busy={busy}
          onClose={() => setConfirm(false)}
          onSubmit={cancel}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

export default DepositReturnDetailPage;
